export class DiffService {
    /** @type {number} Ab dieser Zeilenanzahl wird kein Diff mehr berechnet (Performance) */
    static MAX_DIFF_LINES = 3000;

    /**
     * @typedef {{ type: 'equal' | 'add' | 'remove', value: string }} DiffPart
     * @typedef {{ type: 'equal' | 'add' | 'remove', text: string, inline?: DiffPart[] }} DiffLine
     */

    /**
     * Generisches LCS-Diff auf einer beliebigen Token-Liste.
     * @template T
     * @param {T[]} a
     * @param {T[]} b
     * @param {(x: T, y: T) => boolean} equals
     * @returns {Array<{ type: 'equal' | 'add' | 'remove', value: T }>}
     */
    static diffArray(a, b, equals = (x, y) => x === y) {
        const n = a.length;
        const m = b.length;

        /** @type {Uint32Array[]} */
        const lcs = Array.from({ length: n + 1 }, () => new Uint32Array(m + 1));

        for (let i = 1; i <= n; i++) {
            for (let j = 1; j <= m; j++) {
                lcs[i][j] = equals(a[i - 1], b[j - 1]) ? lcs[i - 1][j - 1] + 1 : Math.max(lcs[i - 1][j], lcs[i][j - 1]);
            }
        }

        /** @type {Array<{ type: 'equal' | 'add' | 'remove', value: T }>} */
        const ops = [];

        let i = n;
        let j = m;

        while (i > 0 || j > 0) {
            if (i > 0 && j > 0 && equals(a[i - 1], b[j - 1])) {
                ops.push({
                    type: 'equal',
                    value: a[i - 1],
                });
                i--;
                j--;
            } else if (j > 0 && (i === 0 || lcs[i][j - 1] >= lcs[i - 1][j])) {
                ops.push({
                    type: 'add',
                    value: b[j - 1],
                });
                j--;
            } else {
                ops.push({
                    type: 'remove',
                    value: a[i - 1],
                });
                i--;
            }
        }

        return ops.reverse();
    }

    /**
     * Wort-/Whitespace-Diff für die Hervorhebung innerhalb einer geänderten Zeile.
     * @param {string} a
     * @param {string} b
     * @returns {DiffPart[]}
     */
    static diffWords(a, b) {
        // Tokens: Wörter UND Whitespace-Runs als eigene Tokens
        // (damit z.B. " " vs "  " sichtbar wird)
        const tokenize = (str) => str.split(/(\s+)/).filter((token) => token !== '');
        const ops = this.diffArray(tokenize(a), tokenize(b), (x, y) => x === y);

        /** @type {DiffPart[]} */
        const merged = [];

        for (const op of ops) {
            const last = merged[merged.length - 1];

            if (last && last.type === op.type) {
                last.value += op.value;
            } else {
                merged.push({
                    type: op.type,
                    value: op.value,
                });
            }
        }

        return merged;
    }

    /**
     * Zeilenbasiertes Diff (Git-/GitHub-Desktop-Stil), mit Wort-Diff für 1:1-Zeilenpaare.
     * @param {string} a Originaltext
     * @param {string} b Bereinigter Text
     * @returns {DiffLine[] | null} null, wenn zu viele Zeilen (Performance)
     */
    static diffLines(a, b) {
        const linesA = a.split('\n');
        const linesB = b.split('\n');

        if (linesA.length > this.MAX_DIFF_LINES || linesB.length > this.MAX_DIFF_LINES) {
            return null;
        }

        const ops = this.diffArray(linesA, linesB, (x, y) => x === y);

        /** @type {DiffLine[]} */
        const result = [];

        /** @type {string[]} */
        let removeBuffer = [];

        /** @type {string[]} */
        let addBuffer = [];

        const flushBuffers = () => {
            if (removeBuffer.length === 0 && addBuffer.length === 0) {
                return;
            }

            // Gleich viele entfernte wie hinzugefügte Zeilen
            // -> als geändertes Paar mit Wort-Diff
            if (removeBuffer.length === addBuffer.length) {
                for (let k = 0; k < removeBuffer.length; k++) {
                    const inline = this.diffWords(removeBuffer[k], addBuffer[k]);

                    result.push({
                        type: 'remove',
                        text: removeBuffer[k],
                        inline,
                    });
                }

                for (let k = 0; k < addBuffer.length; k++) {
                    const inline = this.diffWords(removeBuffer[k], addBuffer[k]);

                    result.push({
                        type: 'add',
                        text: addBuffer[k],
                        inline,
                    });
                }
            } else {
                for (const line of removeBuffer) {
                    result.push({
                        type: 'remove',
                        text: line,
                    });
                }

                for (const line of addBuffer) {
                    result.push({
                        type: 'add',
                        text: line,
                    });
                }
            }

            removeBuffer = [];
            addBuffer = [];
        };

        for (const op of ops) {
            if (op.type === 'remove') {
                removeBuffer.push(op.value);
            } else if (op.type === 'add') {
                addBuffer.push(op.value);
            } else {
                flushBuffers();

                result.push({
                    type: 'equal',
                    text: op.value,
                });
            }
        }

        flushBuffers();

        return result;
    }

    /**
     * @param {?string} a
     * @param {?string} b
     * @returns {{
     *     charsBefore: number,
     *     charsAfter: number,
     *     linesBefore: number,
     *     linesAfter: number,
     *     linesChanged: number,
     *     linesRemoved: number,
     *     linesAdded: number,
     *     changed: boolean,
     * }}
     */
    static getStats(a, b) {
        const subjectA = a ?? '';
        const subjectB = b ?? '';
        const linesDiff = this.diffLines(subjectA, subjectB);

        let linesChanged = 0;
        let linesRemoved = 0;
        let linesAdded = 0;

        if (linesDiff !== null) {
            for (const line of linesDiff) {
                if (line.type === 'remove') {
                    line.inline ? linesChanged++ : linesRemoved++;
                } else if (line.type === 'add' && !line.inline) {
                    linesAdded++;
                }
            }
        }

        const linesBefore = subjectA === '' ? 0 : subjectA.split('\n').length;
        const linesAfter = subjectB === '' ? 0 : subjectB.split('\n').length;

        return {
            charsBefore: subjectA.length,
            charsAfter: subjectB.length,
            linesBefore,
            linesAfter,
            linesChanged,
            linesRemoved,
            linesAdded,
            changed: subjectA !== subjectB,
        };
    }
}
